import type {
  GetCurrentMemberFn,
  GetCurrentUserFn,
  GetCurrentWorkspaceFn,
  GetTeamMemberFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type { Member } from '@/features/iam/members/Member'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type { Observable } from 'rxjs'
import { toStream } from '@/utils/mobx-util'
import { env } from '@/infra/env'
import type { Id } from '@/infra/id'
import type { CursorPaged } from '@/infra/paging'
import type { Team } from '@/features/iam/teams/Team'
import type { BillingMethod } from '@/features/billing/profile/BillingMethod'
import type { BillingProfile } from '@/features/billing/profile/BillingProfile'
import type { BillingTransaction } from '@/features/billing/transactions/BillingTransaction'
import type { Coupon } from '@/features/billing/coupons/Coupon'
import type { UserCoupon } from '@/features/billing/coupons/UserCoupon'
import type { ListCouponsParams } from '@/features/billing/coupons/CouponStore'
import type { CheckoutLink } from '@/features/billing/external-checkout/CheckoutLink'
import type { EmailCheckoutLinkParams } from '@/features/billing/external-checkout/ExternalCheckoutStore'
import type { ProfilePaymentMethodsViewModel } from '@/features/billing/payment-methods/ProfilePaymentMethods/ProfilePaymentMethodsViewModel'
import type { TeamPaymentsViewModel } from '@/features/billing/payment-methods/TeamPayments/TeamPaymentsViewModel'
import type { StripeConnectCallbackModalViewModel } from '@/features/billing/stripe-connect/StripeConnectCallbackModalViewModel'

/**
 * Options for {@link BillingFeature}.
 */
export interface BillingFeatureOptions {
  token$: Observable<string>
  currentMember$: Observable<Member>
  getCurrentWorkspace: GetCurrentWorkspaceFn
  getCurrentUser: GetCurrentUserFn
  getCurrentMember: GetCurrentMemberFn
  getTeamMember: GetTeamMemberFn
  primeWorkspace: PrimeWorkspaceFn
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
}

/**
 * Owner of a billing profile: a member's own profile, or a team's.
 */
export type BillingOwner = Member | Team

/**
 * Fetches the billing transactions with the given IDs.
 */
export type FetchBillingTransactionsFn = (
  ids: string[],
) => Promise<BillingTransaction[]>

/**
 * Streams the loaded billing transactions with the given IDs.
 */
export type BillingTransactionsFn = (
  ids: string[],
) => Observable<BillingTransaction[]>

/**
 * Fetches the billing profile of a member or team.
 */
export type FetchBillingProfileFn = (
  owner: BillingOwner,
) => Promise<BillingProfile>

/**
 * Streams whether the current user may manage the loaded billing profile of
 * a member or team.
 */
export type CanManageBillingFn = (owner: BillingOwner) => Observable<boolean>

/**
 * Fetches the billing methods of a member or team.
 */
export type FetchBillingMethodsFn = (
  owner: BillingOwner,
) => Promise<BillingMethod[]>

/**
 * Streams the loaded billing methods of a member or team.
 */
export type BillingMethodsFn = (
  owner: BillingOwner,
) => Observable<BillingMethod[]>

/**
 * Makes a billing method the default of the current user's profile, or of
 * the team's when given.
 */
export type ChangeDefaultBillingMethodFn = (
  method: BillingMethod,
  team?: Team | null,
) => Promise<BillingProfile>

/**
 * Reactively determines whether billing is enabled in the current workspace.
 */
export type IsBillingEnabledFn = () => boolean

/**
 * Sends the browser to Stripe to connect a payout account.
 */
export type RedirectToStripeConnectFn = () => Promise<void>

/**
 * Prompts for a new payment method. Resolves to `null` when cancelled.
 */
export type NewPaymentMethodFn = (
  member: Member,
  team: Team | null,
  signal?: AbortSignal,
) => Promise<BillingMethod | null>

/**
 * Saves a selected payment method before the selector closes.
 */
export type SavePaymentMethodFn = (method: BillingMethod) => Promise<unknown>

/**
 * Prompts to select a payment method. Resolves to `null` when cancelled.
 */
export type SelectPaymentMethodFn = (
  member: Member,
  team: Team | null,
  currentPaymentMethod: BillingMethod | null,
  onSave: SavePaymentMethodFn | null,
  signal?: AbortSignal,
) => Promise<BillingMethod | null>

/**
 * Gets the Stripe publishable key needed to load the Stripe SDK.
 */
export type GetStripePublishableKeyFn = () => string

/**
 * Adds a card billing method from a Stripe token.
 */
export type AddCardBillingMethodFn = (
  member: Member,
  team: Team | null,
  token: string,
) => Promise<BillingMethod>

/**
 * Adds the coupon with the given code to the member. Resolves to `null` when
 * there is no such coupon.
 */
export type AddCouponFn = (
  member: Member,
  code: string,
) => Promise<UserCoupon | null>

/**
 * Fetches a coupon by ID.
 */
export type FetchCouponFn = (id: Id, signal?: AbortSignal) => Promise<Coupon>

/**
 * Fetches a workspace's coupon by code.
 */
export type FetchCouponByCodeFn = (
  code: string,
  workspaceId: Id,
  signal?: AbortSignal,
) => Promise<Coupon>

/**
 * Lists coupons.
 */
export type ListCouponsFn = (
  query: ListCouponsParams,
  signal?: AbortSignal,
) => Promise<CursorPaged<Coupon>>

/**
 * Fetches the coupons of a member.
 */
export type FetchMemberCouponsFn = (
  member: Member,
  signal?: AbortSignal,
) => Promise<UserCoupon[]>

/**
 * Generates a Stripe checkout link for a job.
 */
export type GenerateCheckoutLinkFn = (jobId: Id) => Promise<CheckoutLink>

/**
 * Emails a job's checkout link.
 */
export type EmailCheckoutLinkFn = (
  payload: EmailCheckoutLinkParams,
) => Promise<void>

/**
 * Invalidates a job's open checkout sessions.
 */
export type InvalidateCheckoutSessionsFn = (
  jobId: Id,
  force: boolean,
) => Promise<void>

/**
 * Creates the view model for a member's own payment methods.
 */
export type ProfilePaymentMethodsViewModelFactory = (
  member: Member,
) => ProfilePaymentMethodsViewModel

/**
 * Creates the view model for a team's payment methods.
 */
export type TeamPaymentsViewModelFactory = (team: Team) => TeamPaymentsViewModel

/**
 * Creates the modal view model that finishes a Stripe Connect flow.
 *
 * @param connectStripe connects the Stripe account for the callback's code
 */
export type StripeConnectCallbackModalViewModelFactory = (
  connectStripe: (code: string) => Promise<unknown>,
) => StripeConnectCallbackModalViewModel

/**
 * Billing-related features.
 */
export type BillingFeature = ReturnType<typeof BillingFeature>

export function BillingFeature(options: BillingFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (m) => m.InternalBillingFeature(options),
  )

  const fetchBillingTransactions: FetchBillingTransactionsFn = internal.wrap(
    (m) => (ids: string[]) => m.transactionStore.fetchBillingTransactions(ids),
  )

  const billingTransactions: BillingTransactionsFn = (ids) =>
    internal.deferUntilLoaded((m) =>
      toStream(
        () => m.transactionStore.filterBillingTransactionsByIds(ids),
        true,
      ),
    )

  const fetchBillingProfile: FetchBillingProfileFn = internal.wrap(
    (m) => m.fetchBillingProfile,
  )

  const canManageBilling: CanManageBillingFn = (owner) =>
    internal.deferUntilLoaded((m) =>
      toStream(() => m.canManageBilling(owner), true),
    )

  const billingProfiles$ = internal.deferUntilLoaded((m) =>
    toStream(() => m.billingProfileStore.billingProfiles.slice(), true),
  )

  const fetchBillingMethods: FetchBillingMethodsFn = internal.wrap(
    (m) => m.fetchBillingMethods,
  )

  const billingMethods: BillingMethodsFn = (owner) =>
    internal.deferUntilLoaded((m) =>
      toStream(() => m.getBillingMethods(owner), true),
    )

  const changeDefaultBillingMethod: ChangeDefaultBillingMethodFn =
    internal.wrap(
      (m) => (method: BillingMethod, team?: Team | null) =>
        m.billingProfileStore.changeDefaultBillingMethod(method, team),
    )

  // Reads the session directly, so callers need not load billing to know
  // whether to show it.
  const isBillingEnabled: IsBillingEnabledFn = () =>
    options.getCurrentWorkspace().config.billing.enabled

  const redirectToStripeConnect: RedirectToStripeConnectFn = async () => {
    const { redirectToStripeConnect } =
      await import('./stripe-connect/stripe-connect')
    redirectToStripeConnect(options.getCurrentWorkspace())
  }

  const promptNewPaymentMethod: NewPaymentMethodFn = internal.wrap(
    (m) => m.promptNewPaymentMethod,
  )

  const promptSelectPaymentMethod: SelectPaymentMethodFn = internal.wrap(
    (m) => m.promptSelectPaymentMethod,
  )

  const getStripePublishableKey: GetStripePublishableKeyFn = () => env.STRIPE_PK

  const addCardBillingMethod: AddCardBillingMethodFn = internal.wrap(
    (m) => (member, team, token) =>
      m.billingProfileStore.addBillingMethodForStripeToken(member, team, token),
  )

  const addCoupon: AddCouponFn = internal.wrap(
    (m) => (member: Member, code: string) =>
      m.couponStore.addCouponToUser(member, code),
  )

  const fetchCoupon: FetchCouponFn = internal.wrap(
    (m) => (id: Id, signal?: AbortSignal) =>
      m.couponStore.fetchCoupon(id, signal),
  )

  const fetchCouponByCode: FetchCouponByCodeFn = internal.wrap(
    (m) => (code: string, workspaceId: Id, signal?: AbortSignal) =>
      m.couponStore.fetchCouponByCode(code, workspaceId, signal),
  )

  const listCoupons: ListCouponsFn = internal.wrap(
    (m) => (query: ListCouponsParams, signal?: AbortSignal) =>
      m.couponStore.listCoupons(query, signal),
  )

  const fetchMemberCoupons: FetchMemberCouponsFn = internal.wrap(
    (m) => (member: Member, signal?: AbortSignal) =>
      m.couponStore.fetchUserCoupons(member, signal),
  )

  const generateCheckoutLink: GenerateCheckoutLinkFn = internal.wrap(
    (m) => (jobId: Id) => m.externalCheckoutStore.generateCheckoutLink(jobId),
  )

  const emailCheckoutLink: EmailCheckoutLinkFn = internal.wrap(
    (m) => (payload: EmailCheckoutLinkParams) =>
      m.externalCheckoutStore.emailCheckoutLink(payload),
  )

  const invalidateCheckoutSessions: InvalidateCheckoutSessionsFn =
    internal.wrap(
      (m) => (jobId: Id, force: boolean) =>
        m.externalCheckoutStore.invalidateCheckoutSessions(jobId, force),
    )

  return {
    // Internal
    internal,

    // Public
    fetchBillingTransactions,
    billingTransactions,
    fetchBillingProfile,
    canManageBilling,
    billingProfiles$,
    fetchBillingMethods,
    billingMethods,
    changeDefaultBillingMethod,
    isBillingEnabled,
    redirectToStripeConnect,
    promptNewPaymentMethod,
    promptSelectPaymentMethod,
    getStripePublishableKey,
    addCardBillingMethod,
    addCoupon,
    fetchCoupon,
    fetchCouponByCode,
    listCoupons,
    fetchMemberCoupons,
    generateCheckoutLink,
    emailCheckoutLink,
    invalidateCheckoutSessions,
    createProfilePaymentMethodsViewModel: internal.lazy(
      (m) => m.createProfilePaymentMethodsViewModel,
    ),
    createTeamPaymentsViewModel: internal.lazy(
      (m) => m.createTeamPaymentsViewModel,
    ),
    createStripeConnectCallbackModalViewModel: internal.lazy(
      (m) => m.createStripeConnectCallbackModalViewModel,
    ),
  }
}
